import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';

import type { ArloRegionCodes } from '#services/arlo';
import { GeolocationContext } from '#providers/GeolocationProvider';
import { Text } from '@/components/Text';

type Region = {
  value: ArloRegionCodes,
  label: string,
};

const currencies: Region[] = [
  {
    value: 'us',
    label: 'United States',
  },
  {
    value: 'ca',
    label: 'Canada',
  },
  {
    value: 'mx',
    label: 'Mexico',
  },
  {
    value: 'uk',
    label: 'United Kingdom',
  },
];

export type RegionSelectorProps = {
  defaultValue: ArloRegionCodes | null,
};

const translationKey = 'Components.RegionSelector';

const RegionSelector = ({
  defaultValue,
}: RegionSelectorProps) => {
  const { region, setRegion } = useContext(GeolocationContext);
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);

  const regionChangeHandler = useCallback(
    (value: ArloRegionCodes) => {
      setRegion(value);
      setIsOpen(false);
    },
    [setRegion],
  );

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const currentValue = region ?? defaultValue ?? currencies[0].value;
  const currentLabel = currencies.find((c) => c.value === currentValue)?.label || '';

  return (
    <div
      className="flex flex-col items-start max-w-[200px]"
      data-testid="region-selector"
      ref={selectorRef}
    >
      <div className="mb-1">
        <Text variant="caption">
          <b>
            {t(`${translationKey}.ResidentOf`)}
          </b>
        </Text>
      </div>
      <div className="relative w-[200px]">
        <button
          type="button"
          className="w-full h-12 px-3 py-3 bg-white cursor-pointer rounded-md focus:outline-none border-none ring-1 ring-impgray100 focus:ring-2 focus:ring-impblue300"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="region-selector-input"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <div className="flex justify-between items-center">
            <span className="text-impblue300 text-left">
              <Text variant="body3" className="text-impblue300 !mb-0">
                {currentLabel}
              </Text>
            </span>
            <svg
              className={`w-4 h-4 transition-transform text-impblue300 ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {isOpen && (
          <div
            className="absolute z-10 w-full mt-1 py-2 bg-white rounded-md"
            role="listbox"
            aria-labelledby="region-selector"
          >
            {currencies.map((option: Region) => (
              <button
                key={option.value}
                className={`w-full px-3 py-2 text-left cursor-pointer hover:bg-gray-100 appearance-none border-none bg-transparent ${
                  currentValue === option.value ? 'bg-blue-50' : ''
                }`}
                onClick={() => regionChangeHandler(option.value)}
                data-testid={`region-option-${option.value}`}
                role="option"
                aria-selected={currentValue === option.value}
                type="button"
              >
                <Text variant="body3" className="text-impblue300 text-left w-full !mb-0">
                  {option.label}
                </Text>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RegionSelector;
